/* KD9 Mobile — compose navigation from the canonical backend access map.
 * UI grouping only; actual route authorization remains server-side. */
import { ROLE_MENUS, getAcademicAccessSection, getDelegatedPermissionSection, isMenuItemAllowed, moduleForPath } from "@/lib/roleMenus";

export const MOBILE_MODE_KEY = "kd9-app-mobile-shell";
export const PRIVILEGED_ROLES = new Set(["director", "admin", "support"]);
const MOBILE_ROLES = ["user", "estudiante", "consultor", "creador"];
const MOBILE_ALLOWED_PATHS = new Set(MOBILE_ROLES.flatMap((role) =>
  (ROLE_MENUS[role]?.sections || []).flatMap((section) => (section.items || []).map((item) => item.to)),
));

export function mobileModeEnabled() {
  if (typeof window === "undefined") return false;
  try { return window.sessionStorage.getItem(MOBILE_MODE_KEY) === "1"; }
  catch (_) { return false; }
}

export function isAdministrativeAccount(accessMap) {
  return (accessMap?.roles || []).some((role) => PRIVILEGED_ROLES.has(role));
}

function visible(item, permissions, accessMap) {
  if (!item?.to?.startsWith("/app/") || !MOBILE_ALLOWED_PATHS.has(item.to)) return false;
  if (!isMenuItemAllowed(item, permissions)) return false;
  if (item.flagRequired && !accessMap?.flags?.[item.flagRequired]) return false;
  const mod = moduleForPath(item.to);
  return !mod || accessMap?.modules?.[mod] !== false;
}

/** One source of truth for mobile search and quick shortcuts: existing role menus. */
export function mobileCatalog(role, accessMap) {
  const permissions = new Set(accessMap?.permissions || []);
  const delegated = getDelegatedPermissionSection(role, permissions);
  const academic = getAcademicAccessSection(role, accessMap);
  const sections = [...(ROLE_MENUS[role]?.sections || []), ...(academic ? [academic] : []), ...(delegated ? [delegated] : [])];
  const seen = new Set();
  return sections.map((section) => ({
    label: section.sectionKey || section.label || "Mis herramientas",
    items: (section.items || []).filter((item) => {
      if (!visible(item, permissions, accessMap) || seen.has(item.to)) return false;
      seen.add(item.to);
      return true;
    }),
  })).filter((section) => section.items.length > 0);
}

/** Keep five predictable top-level destinations, with no inaccessible shortcut. */
export function mobileTabs(role, accessMap) {
  const available = new Set(mobileCatalog(role, accessMap).flatMap((s) => s.items.map((i) => i.to)));
  const preferredWork = role === "consultor"
    ? ["/app/bolsa-diagnosticos", "/app/bolsa-consultas", "/app/mis-trabajos"]
    : role === "creador"
      ? ["/app/mis-productos", "/app/pedidos-marketplace", "/app/contenidos-academia"]
      : ["/app/mis-diagnosticos", "/app/mis-cursos", "/app/energia-kd"];
  const work = preferredWork.find((p) => available.has(p)) || "/app/energia-kd";
  const liveEnabled = accessMap?.modules?.temple_live !== false;
  return [
    { label: "Inicio", icon: "home", to: "/app/inicio" },
    { label: "Explorar", icon: "compass", to: "/app/explorar-movil" },
    { label: role === "consultor" || role === "creador" ? "Trabajo" : "Mi KD9", icon: "briefcase", to: work },
    liveEnabled
      ? { label: "En vivo", icon: "radio", to: "/app/templo-vivo" }
      : { label: "Mensajes", icon: "radio", to: "/app/mensajes" },
    { label: "Perfil", icon: "user", to: available.has("/app/perfil") ? "/app/perfil" : "/app/inicio" },
  ];
}
